<script setup>
import { useStationStore } from '@/stores/station'
import { useAbility } from '@casl/vue'

definePage({
  meta: {
    layout: 'default',
    action: 'read',
    subject: 'Station',
  },
})

const ability = useAbility()
const store = useStationStore()

const isLoading = ref(true)
const loadError = ref('')

const headers = [
  { title: 'Nama', key: 'name' },
  { title: 'Tipe', key: 'type' },
  { title: 'Printer', key: 'printer' },
  { title: 'Default', key: 'is_default' },
  { title: 'Urutan', key: 'sort_order' },
  { title: 'Aktif', key: 'is_active' },
  { title: '', key: 'actions', sortable: false, align: 'end' },
]

const typeOptions = [
  { title: 'Dapur (Kitchen)', value: 'kitchen' },
  { title: 'Minuman (Beverage)', value: 'beverage' },
  { title: 'Dessert', value: 'dessert' },
  { title: 'Lainnya', value: 'other' },
]

const printerName = printerId => store.printers.find(p => p.id === printerId)?.name || '—'

const load = async () => {
  isLoading.value = true
  loadError.value = ''
  try {
    await Promise.all([store.fetchStations(), store.fetchPrinters()])
  }
  catch (err) {
    loadError.value = err?.data?.message || err?.message || 'Gagal memuat data stasiun.'
  }
  finally {
    isLoading.value = false
  }
}

onMounted(load)

// ── Form dialog (create/edit) ────────────────────────────────────────────

const isDialogOpen = ref(false)
const isEditing = ref(false)
const isSubmitting = ref(false)
const fieldErrors = ref({})
const editingId = ref(null)

const form = ref({
  name: '',
  type: 'kitchen',
  printer_id: null,
  is_default: false,
  sort_order: 0,
  is_active: true,
})

const resetForm = () => {
  form.value = { name: '', type: 'kitchen', printer_id: null, is_default: false, sort_order: 0, is_active: true }
  fieldErrors.value = {}
}

const openCreate = () => {
  isEditing.value = false
  editingId.value = null
  resetForm()
  isDialogOpen.value = true
}

const openEdit = station => {
  isEditing.value = true
  editingId.value = station.id
  form.value = {
    name: station.name,
    type: station.type,
    printer_id: station.printer_id,
    is_default: station.is_default,
    sort_order: station.sort_order,
    is_active: station.is_active,
  }
  fieldErrors.value = {}
  isDialogOpen.value = true
}

const submitForm = async () => {
  isSubmitting.value = true
  fieldErrors.value = {}

  try {
    if (isEditing.value)
      await store.updateStation(editingId.value, form.value)
    else
      await store.createStation(form.value)

    isDialogOpen.value = false
  }
  catch (err) {
    if (err?.status === 422 || err?.response?.status === 422)
      fieldErrors.value = err?.data?.errors || {}
  }
  finally {
    isSubmitting.value = false
  }
}

// ── Delete confirm ────────────────────────────────────────────────────────

const isDeleteDialogOpen = ref(false)
const deletingStation = ref(null)
const isDeleting = ref(false)

const askDelete = station => {
  deletingStation.value = station
  isDeleteDialogOpen.value = true
}

const confirmDelete = async () => {
  isDeleting.value = true
  try {
    await store.deleteStation(deletingStation.value.id)
    isDeleteDialogOpen.value = false
  }
  finally {
    isDeleting.value = false
  }
}
</script>

<template>
  <div>
    <div class="d-flex align-center justify-space-between mb-4">
      <h4 class="text-h4 mb-0">
        Stasiun Dapur/Barista
      </h4>
      <VBtn v-if="ability.can('create', 'Station')" prepend-icon="ri-add-line" @click="openCreate">
        Tambah Stasiun
      </VBtn>
    </div>

    <VAlert v-if="loadError" type="error" variant="tonal" class="mb-4">
      {{ loadError }}
    </VAlert>

    <VCard>
      <VDataTable :headers="headers" :items="store.stations" :loading="isLoading" item-value="id">
        <template #item.type="{ item }">
          <VChip size="small" variant="tonal">
            {{ typeOptions.find(t => t.value === item.type)?.title || item.type }}
          </VChip>
        </template>
        <template #item.printer="{ item }">
          {{ printerName(item.printer_id) }}
        </template>
        <template #item.is_default="{ item }">
          <VIcon v-if="item.is_default" icon="ri-check-line" color="success" />
        </template>
        <template #item.is_active="{ item }">
          <VChip size="small" :color="item.is_active ? 'success' : 'secondary'" variant="tonal">
            {{ item.is_active ? 'Aktif' : 'Nonaktif' }}
          </VChip>
        </template>
        <template #item.actions="{ item }">
          <VBtn
            :to="{ name: 'kds-station-id', params: { stationId: item.id } }"
            icon
            size="small"
            variant="text"
          >
            <VIcon icon="ri-tv-2-line" />
            <VTooltip activator="parent">
              Buka Monitor KDS
            </VTooltip>
          </VBtn>
          <VBtn v-if="ability.can('update', 'Station')" icon size="small" variant="text" @click="openEdit(item)">
            <VIcon icon="ri-edit-line" />
          </VBtn>
          <VBtn v-if="ability.can('delete', 'Station')" icon size="small" variant="text" color="error" @click="askDelete(item)">
            <VIcon icon="ri-delete-bin-line" />
          </VBtn>
        </template>
      </VDataTable>
    </VCard>

    <!-- Form dialog -->
    <VDialog v-model="isDialogOpen" max-width="480">
      <VCard>
        <VCardItem>
          <VCardTitle>{{ isEditing ? 'Edit Stasiun' : 'Tambah Stasiun' }}</VCardTitle>
        </VCardItem>
        <VCardText>
          <VForm @submit.prevent="submitForm">
            <VRow>
              <VCol cols="12">
                <VTextField v-model="form.name" label="Nama Stasiun" :error-messages="fieldErrors.name" />
              </VCol>
              <VCol cols="12">
                <VSelect v-model="form.type" :items="typeOptions" label="Tipe" :error-messages="fieldErrors.type" />
              </VCol>
              <VCol cols="12">
                <VSelect
                  v-model="form.printer_id"
                  :items="store.printers"
                  item-title="name"
                  item-value="id"
                  label="Printer (opsional)"
                  clearable
                  :error-messages="fieldErrors.printer_id"
                />
              </VCol>
              <VCol cols="6">
                <VTextField v-model.number="form.sort_order" type="number" label="Urutan" :error-messages="fieldErrors.sort_order" />
              </VCol>
              <VCol cols="6" class="d-flex align-center gap-4">
                <VSwitch v-model="form.is_default" label="Default" />
                <VSwitch v-model="form.is_active" label="Aktif" />
              </VCol>
              <VCol cols="12" class="d-flex justify-end gap-2">
                <VBtn variant="tonal" @click="isDialogOpen = false">
                  Batal
                </VBtn>
                <VBtn type="submit" :loading="isSubmitting">
                  Simpan
                </VBtn>
              </VCol>
            </VRow>
          </VForm>
        </VCardText>
      </VCard>
    </VDialog>

    <!-- Delete confirm -->
    <VDialog v-model="isDeleteDialogOpen" max-width="400">
      <VCard>
        <VCardText>
          <p class="mb-0">
            Hapus stasiun "{{ deletingStation?.name }}"? Tindakan ini tidak bisa dibatalkan.
          </p>
        </VCardText>
        <VCardActions class="justify-end">
          <VBtn variant="tonal" @click="isDeleteDialogOpen = false">
            Batal
          </VBtn>
          <VBtn color="error" :loading="isDeleting" @click="confirmDelete">
            Hapus
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>
  </div>
</template>
